import { useEffect, useState } from "react";
import { getService } from "../services/axios/services";
import { getAccessToken } from "../shared/constants/login-store";

// `enabled` (gate) lets callers that don't fetch on mount - e.g. a modal
// provider mounted once at app root - defer the request until it's actually
// needed (see release-flow.md) instead of firing it eagerly at mount time,
// when auth may not have hydrated yet. Refetches every time it flips
// false -> true, so each open gets a fresh value.
export function useFeatureFlag(featureName, { enabled: gate = true } = {}) {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(gate);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!gate) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchFeatureFlag() {
      // Avoid firing authenticated requests when there's no session (e.g. on
      // /auth or right after logout) - an unauthenticated call here would 401,
      // trigger the refresh-token flow with a null refresh token, and loop.
      if (!getAccessToken()) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const response = await getService({
          endpoint: `Features/${featureName}`,
        });

        if (!cancelled) {
          setEnabled(Boolean(response.isEnabled));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setEnabled(false);

          throw new Error(
            `Não foi possível carregar a feature flag: ${featureName}`
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchFeatureFlag();

    return () => {
      cancelled = true;
    };
  }, [featureName, gate]);

  return {
    enabled,
    loading,
    error,
  };
}
